/**
 * Variant rules through ffish, Fairy-Stockfish's move generator compiled to WebAssembly.
 * It decides legal moves (drops, explosions, forced captures, Chess960 castling),
 * SAN and results. The browser loads it from /engine/variants at runtime, never from
 * the app bundle; tests pass in a Node instance.
 *
 * Positions are always rebuilt from the start FEN plus the move list, so repetition
 * counts are exact and any earlier position can be inspected.
 */
import type { Board, FairyStockfish } from "ffish-es6";
import type { PieceSymbol } from "chess.js";
import type { GameResult } from "@/lib/chess/game";
import type { PlayableVariant } from "./config";
import { checksRemaining, chess960Fen, findKing, parsePlacement, sideToMove } from "./fen";

export type Ffish = FairyStockfish;

export type VariantMove = {
  san: string;
  uci: string;
  from: string;
  to: string;
  /** Position after the move. */
  fen: string;
  captured: boolean;
  check: boolean;
  drop?: PieceSymbol;
};

export type VariantPosition = {
  fen: string;
  turn: "w" | "b";
  /** UCI moves: "e2e4", "e7e8q", drops "P@e4", Chess960 castling as king-takes-rook ("e1h1"). */
  legal: string[];
  check: boolean;
  checkSquare: string | null;
  result: GameResult | null;
};

const ENGINE_DIR = "/engine/variants/";
let loading: Promise<Ffish> | null = null;

/** The page's ffish instance, loaded on first use in the browser. */
export function loadFfish(): Promise<Ffish> {
  if (typeof window === "undefined") return Promise.reject(new Error("ffish loads in the browser only"));
  if (!loading) {
    const url = `${ENGINE_DIR}ffish.js`;
    loading = import(/* webpackIgnore: true */ url)
      .then((mod: { default: (opts: object) => Promise<Ffish> }) => mod.default({ locateFile: (file: string) => `${ENGINE_DIR}${file}` }));
    loading.catch(() => { loading = null; });
  }
  return loading;
}

function withBoard<T>(ff: Ffish, variant: PlayableVariant, startFen: string, moves: readonly string[], fn: (b: Board) => T): T {
  const board = new ff.Board(variant.rulesId, startFen, !!variant.chess960);
  try {
    for (const m of moves) if (!board.push(m)) throw new Error(`Illegal move ${m} in ${variant.id}`);
    return fn(board);
  } finally {
    board.delete();
  }
}

/** Start position: a random (or chosen) Chess960 setup, otherwise the variant's own. */
export function variantStartFen(ff: Ffish, variant: PlayableVariant, chess960Index?: number): string {
  if (variant.chess960) return chess960Fen(chess960Index ?? Math.floor(Math.random() * 960));
  const board = new ff.Board(variant.rulesId);
  try { return board.fen(); } finally { board.delete(); }
}

/**
 * True when ffish reads the FEN back unchanged and the side to move has a move.
 * (ffish's own validateFen rejects even the standard start position in this build.)
 */
export function isPlayablePosition(ff: Ffish, rulesId: string, fen: string, chess960 = false): boolean {
  const board = new ff.Board(rulesId, fen, chess960);
  try { return board.fen() === fen.trim() && legalList(board).length > 0; } finally { board.delete(); }
}

export function legalList(board: Board): string[] {
  return board.legalMoves().split(" ").filter(Boolean);
}

const HILL = new Set(["d4", "e4", "d5", "e5"]);

/** Result of a finished position in words that follow "won by" or "Draw by". */
export function variantResult(board: Board, variant: PlayableVariant): GameResult | null {
  if (!board.isGameOver(true)) return null;
  const raw = board.result(true);
  const winner = raw === "1-0" ? "w" : raw === "0-1" ? "b" : null;
  const fen = board.fen();
  const pieces = [...parsePlacement(fen).entries()];
  const count = (color: "w" | "b") => pieces.filter(([, p]) => p.color === color).length;
  const kings = pieces.filter(([, p]) => p.type === "k");
  const noMoves = legalList(board).length === 0;

  switch (variant.id) {
    case "kingofthehill":
      if (kings.some(([sq]) => HILL.has(sq))) return { winner, reason: "reaching the hill" };
      break;
    case "racingkings":
      if (kings.some(([sq]) => sq[1] === "8")) return winner ? { winner, reason: "reaching the finish line" } : { winner: null, reason: "both kings reaching the finish line" };
      break;
    case "threecheck": {
      const left = checksRemaining(fen);
      if (left && (left.w === 0 || left.b === 0)) return { winner, reason: "three checks" };
      break;
    }
    case "atomic":
      if (kings.length < 2) return { winner, reason: "exploding the king" };
      break;
    case "antichess":
      if (winner && count(winner) === 0) return { winner, reason: "losing every piece" };
      if (winner && noMoves) return { winner, reason: "running out of moves" };
      break;
    case "horde":
      if (count("w") === 0) return { winner: "b", reason: "wiping out the horde" };
      break;
  }
  if (noMoves && board.isCheck()) return { winner, reason: "checkmate" };
  if (noMoves && winner === null) return { winner: null, reason: "stalemate" };
  if (winner === null) {
    if (board.isInsufficientMaterial()) return { winner, reason: "insufficient material" };
    if (board.halfmoveClock() >= 100) return { winner, reason: "fifty-move rule" };
    return { winner, reason: "repetition" };
  }
  return { winner, reason: board.isCheck() ? "checkmate" : "the variant’s rules" };
}

function describeBoard(board: Board, variant: PlayableVariant): VariantPosition {
  const fen = board.fen();
  const turn = sideToMove(fen);
  const result = variantResult(board, variant);
  const check = board.isCheck();
  return { fen, turn, legal: result ? [] : legalList(board), check, checkSquare: check ? findKing(fen, turn) : null, result };
}

export function describePosition(ff: Ffish, variant: PlayableVariant, startFen: string, moves: readonly string[]): VariantPosition {
  return withBoard(ff, variant, startFen, moves, (b) => describeBoard(b, variant));
}

/** Plays one move after the given history, or returns null if it is illegal. */
export function playVariantMove(ff: Ffish, variant: PlayableVariant, startFen: string, moves: readonly string[], uci: string): { move: VariantMove; position: VariantPosition } | null {
  return withBoard(ff, variant, startFen, moves, (b) => {
    if (!legalList(b).includes(uci) || b.isGameOver(true)) return null;
    const san = b.sanMove(uci);
    const captured = b.isCapture(uci);
    if (!b.push(uci)) return null;
    const drop = uci[1] === "@" ? (uci[0].toLowerCase() as PieceSymbol) : undefined;
    const to = uci.slice(2, 4);
    const move: VariantMove = { san, uci, from: drop ? to : uci.slice(0, 2), to, fen: b.fen(), captured, check: b.isCheck(), ...(drop ? { drop } : {}) };
    return { move, position: describeBoard(b, variant) };
  });
}

/** The UCI string for a board gesture, if it is legal: a drop, a promotion, or a plain move. */
export function intentToUci(legal: readonly string[], intent: { from: string; to: string; promotion?: string; drop?: string }): string | null {
  if (intent.drop) return legal.find((m) => m[1] === "@" && m[0].toLowerCase() === intent.drop && m.slice(2, 4) === intent.to) ?? null;
  const uci = `${intent.from}${intent.to}${intent.promotion ?? ""}`;
  return legal.includes(uci) ? uci : null;
}

/** The engine's "position" command for this game (multi-variant Stockfish understands startpos per variant). */
export function enginePosition(variant: PlayableVariant, startFen: string, moves: readonly string[]): string {
  const base = variant.chess960 ? `fen ${startFen}` : "startpos";
  return moves.length ? `${base} moves ${moves.join(" ")}` : base;
}
